/**
 * Contract status lifecycle.
 *
 * A contract starts in DRAFT, moves through signature, and ends as ACTIVE
 * (or DECLINED / CANCELLED if it never gets there). A client with no ACTIVE
 * contract is effectively without service.
 *
 * PAUSED is a reversible detour off ACTIVE: billing is suspended and the
 * contract's PENDING invoices are parked until it is unpaused back to ACTIVE.
 */
export type ContractStatus =
  | 'DRAFT'
  | 'PENDING_SIGNATURE'
  | 'SIGNED'
  | 'ACTIVE'
  | 'DECLINED'
  | 'CANCELLED'
  | 'PAUSED';

/**
 * Proof material from the e-signature provider (e.g. DocuSign envelope info,
 * signer IP, signature timestamp).
 */
export interface SignatureProof {
  envelopeId: string;
  signerEmail: string;
  signedAt: string | null;
  signerIp: string | null;
}

/** The client's primary contact, used as the default signer. */
export interface SignerContact {
  email: string;
  name: string;
}

/**
 * Bench client the contract belongs to. `id` is the canonical Bench client ID
 * (e.g. "4689") used across the rest of the platform.
 */
export interface Client {
  id: string;
  businessName: string;
}

/**
 * Contract — every service sold to a client.
 *
 * There is only one contract for a given client at a given date. A contract
 * is immutable once finalized. Renewing/updating the service agreement creates
 * a new contract and voids the old one. Contracts typically last one year.
 */
export interface Contract {
  id: string;
  client: Client;
  startDate: string;
  endDate: string;
  status: ContractStatus;
  /** Short, free-form descriptor entered by sales to distinguish a client's contracts at a glance. */
  nickname?: string | null;
  /** Reason the recipient gave when declining, if any. Selected by both the list and details queries. */
  declineReason?: string | null;
  /** The client's primary contact, used to pre-fill the signer fields. Only fetched on the details query. */
  defaultSigner?: SignerContact | null;
  pdfTemplateVersion: string;
  pdfS3Url: string;
  signatureProof: SignatureProof | null;
  /** External reference to the originating Salesforce Opportunity. */
  salesforceOpportunity: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Pagination metadata, shared verbatim with the other subgraphs.
 *
 * The contracts query pages by offset, so `startCursor`/`endCursor` are always
 * null there. `total` is nullable only because the type is shared — prefer
 * `PaginatedContracts.totalCount`, which is non-null.
 */
export interface PageInfo {
  startCursor: string | null;
  endCursor: string | null;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  total: number | null;
}

/** One page of contracts, ordered createdAt DESC (id DESC as tiebreaker). */
export interface PaginatedContracts {
  items: Contract[];
  pageInfo: PageInfo;
  /** Contracts matching the filter across every page — not the size of `items`. */
  totalCount: number;
}

/**
 * Server-side filter for the contracts query. Every field combines with AND,
 * and each list matches any of its values.
 *
 * Two traps worth remembering when building this object:
 *   - An empty list matches *nothing*. Omit a facet the user hasn't narrowed
 *     rather than sending `[]`.
 *   - `createdBy` and `hasNoCreator` are contradictory and together match
 *     nothing, so the UI only ever sends one of them.
 */
export interface ContractFilterInput {
  /** Case-insensitive substring match on the client's business name and the contract nickname. */
  search?: string;
  /** Exact Bench client IDs. */
  clientIds?: string[];
  statuses?: ContractStatus[];
  /** Exact Bench usernames (emails) of the creators. */
  createdBy?: string[];
  /** True to return only contracts with no recorded creator (background jobs, or pre-tracking). */
  hasNoCreator?: boolean;
}

/**
 * Product line.
 *
 * Internal codes map to customer-facing names:
 *   KU  → Keep Up
 *   CU  → Catch Up
 *   TAX → Bench Tax
 *   PRO → Pro
 */
export type ProductLine = 'KEEP_UP' | 'CATCH_UP' | 'BENCH_TAX' | 'QBO_BOOKKEEPING';

export const PRODUCT_LINE_LABEL: Record<ProductLine, string> = {
  KEEP_UP: 'Keep Up',
  CATCH_UP: 'Catch Up',
  BENCH_TAX: 'Bench Tax',
  QBO_BOOKKEEPING: 'QBO',
};

/**
 * Soft background + readable text colour per product line, used to visually
 * distinguish the catalogue's filter chips and section headers so scanning /
 * filtering is easier on the eyes.
 */
export const PRODUCT_LINE_COLOR: Record<ProductLine, { bg: string; text: string }> = {
  KEEP_UP: { bg: '#E7F0FE', text: '#1B5FBF' },
  CATCH_UP: { bg: '#FDF0E1', text: '#B65A16' },
  BENCH_TAX: { bg: '#E6F4EA', text: '#1E7B45' },
  QBO_BOOKKEEPING: { bg: '#F0E9FB', text: '#6D3BBF' },
};

/** Payment cadence for a product. */
export type PaymentCadence =
  | 'ONE_TIME'
  | 'WEEKLY'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'SEMI_ANNUAL'
  | 'ANNUAL'
  | 'ANNUAL_2_INSTALLMENT'
  | 'ANNUAL_3_INSTALLMENT'
  | 'ANNUAL_4_INSTALLMENT'
  | 'ONE_TIME_2_INSTALLMENT'
  | 'ONE_TIME_3_INSTALLMENT'
  | 'ONE_TIME_4_INSTALLMENT';

export const PAYMENT_CADENCE_LABEL: Record<PaymentCadence, string> = {
  ONE_TIME: 'One-time',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  SEMI_ANNUAL: 'Semi-annual',
  ANNUAL: 'Annual',
  ANNUAL_2_INSTALLMENT: 'Annual (2 installments)',
  ANNUAL_3_INSTALLMENT: 'Annual (3 installments)',
  ANNUAL_4_INSTALLMENT: 'Annual (4 installments)',
  ONE_TIME_2_INSTALLMENT: 'One-time (2 installments)',
  ONE_TIME_3_INSTALLMENT: 'One-time (3 installments)',
  ONE_TIME_4_INSTALLMENT: 'One-time (4 installments)',
};

/**
 * How a product is billed: a recurring subscription, a single up-front charge, or
 * hourly/usage. These match the backend GraphQL `BillingType` enum verbatim — the
 * product group key depends on the runtime value, so they must stay in sync.
 */
export type BillingType = 'SUBSCRIPTION' | 'ONE_TIME' | 'HOURLY';

export const BILLING_TYPE_LABEL: Record<BillingType, string> = {
  SUBSCRIPTION: 'Subscription',
  ONE_TIME: 'One-time',
  HOURLY: 'Hourly',
};

/**
 * How the ContractContent discount field should be interpreted.
 *
 *  - PERCENT_OFF — discountAmount is a percentage (0-100) off the line subtotal
 *  - AMOUNT_OFF  — discountAmount is a flat dollar amount off the line subtotal
 *
 * A `null` discountType means no discount applies.
 */
export type DiscountType = 'PERCENT_OFF' | 'AMOUNT_OFF';

/**
 * ContractContent — links a Contract to a Product sold under it.
 *
 * A given contract can contain several different products (e.g. Keep Up +
 * Bench Tax). When the contract is converted into invoices, each
 * ContractContent eventually maps to one invoice line item per billing period.
 *
 * startDate / endDate bound the coverage window; the number of months covered
 * is derived from that range.
 *
 * discountDuration is the number of payment periods the discount applies for
 * (e.g. 3 = first 3 payments are discounted).
 */
export interface ContractContent {
  id: string;
  startDate: string;
  endDate: string;
  /** Number of SKU units on this line — any positive fractional value, 2 decimal places. Defaults to 1 when not set by the operator. */
  quantity: number;
  discountType: DiscountType | null;
  discountAmount: number;
  discountDuration: number;
}

/**
 * Product — a sellable SKU. A contract is composed of one or more products.
 *
 * The SKU is the canonical unique identifier (alphanumeric, human-readable).
 * Price is per payment, with the cadence indicating how often that payment recurs.
 *
 * Each product belongs to a campaign (e.g. "NB1") — campaigns group SKUs that
 * were launched together for a particular promotion or pricing rollout. The
 * campaign is encoded as the SKU's prefix.
 */
export interface Product {
  sku: string;
  displayName: string;
  name: string;
  campaign: string;
  productLine: ProductLine;
  billingType: BillingType;
  cadence: PaymentCadence;
  /** Price per payment, in USD. */
  pricePerPayment: number;
  canBeSold: boolean;
}

/**
 * Input shape for the createContract mutation.
 */
export interface CreateContractInput {
  clientId: string;
  startDate: string;
  endDate: string;
  salesforceOpportunity: string;
  /** Optional; omit when the operator hasn't provided one. */
  nickname?: string;
}

export interface SendContractForSignatureInput {
  contractId: string;
  /** Optional override; omit to use the client's primary contact. */
  signerEmail?: string;
  signerName?: string;
}

export interface AddContractContentInput {
  contractId: string;
  productSku: string;
  startDate: string;
  endDate: string;
  /** Required on create; caller supplies 1 when the operator hasn't chosen otherwise. Fractional, 2 decimal places. */
  quantity: number;
  discountType: DiscountType | null;
  discountAmount: number;
  discountDuration: number;
}

export interface UpdateContractContentInput {
  id: string;
  productSku: string;
  startDate: string;
  endDate: string;
  /** Optional on update; omit to leave the current value untouched. Fractional, 2 decimal places. */
  quantity?: number;
  discountType: DiscountType | null;
  discountAmount: number;
  discountDuration: number;
}

/**
 * Input shape for the cancelContract mutation. Cancellation is permanent and
 * applies to an ACTIVE or PAUSED contract.
 *
 * `reason` is optional to the API (max 500 chars) but the operator UI requires
 * it: a cancellation can't be undone, so the operator note is the only record
 * of why it happened.
 */
export interface CancelContractInput {
  contractId: string;
  reason?: string;
}

/**
 * Invoice lifecycle status.
 *
 * Invoices are generated by the backend when a contract is finalized — one per
 * scheduled charge. Invoice contents are immutable; to change what's sold, a
 * new contract must be created.
 *
 *  - PENDING        — scheduled but not yet charged
 *  - PROCESSING     — charge attempt in flight; a stuck invoice in this state
 *                     (e.g. lambda timeout mid-charge) needs an operator to
 *                     verify with the payment processor and reset manually
 *  - PAID           — successfully charged
 *  - PAYMENT_DENIED — charge attempt failed (card declined, etc.)
 *  - VOIDED         — cancelled before payment (e.g. client churned, contract renegotiated)
 *  - REFUNDED       — paid and later refunded (e.g. work not performed)
 *  - PAUSED         — parked while the contract is PAUSED; not charged until
 *                     the contract is unpaused, which returns it to PENDING
 *  - CANCELLED      — swept here when the parent contract was cancelled. Like
 *                     VOIDED it is terminal and never charged, but it is only
 *                     ever set in bulk by cancelContract, never per-invoice
 */
export type InvoiceStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'PAID'
  | 'PAYMENT_DENIED'
  | 'VOIDED'
  | 'REFUNDED'
  | 'PAUSED'
  | 'CANCELLED';

/**
 * How an invoice was created — automatically from a finalized contract, or
 * raised by hand by a human operator.
 */
export type InvoiceOrigin = 'CONTRACT' | 'MANUAL' | 'MIGRATED';

/**
 * Outcome of the payment processor's charge attempt for an invoice. Tracked
 * independently of the invoice `status` so we can surface processor-side state
 * (e.g. a card decline) without changing the invoice's lifecycle.
 */
export type ChargeStatus = 'PENDING' | 'PAID' | 'PAYMENT_DENIED' | 'VOIDED' | 'REFUNDED';

/**
 * Outcome of a single charge attempt against an invoice.
 *
 *  - APPROVED — Helcim returned a body with that status
 *  - DECLINED — Helcim returned a body with that status
 *  - ERROR    — the call to Helcim threw (network / timeout / 5xx / non-2xx with a
 *               body). May still carry the helcim* fields when the error body
 *               wrapped an underlying declined transaction.
 *  - REJECTED — a post-load, pre-Helcim guard fired (wrong status, zero total, tax
 *               skip, markProcessing race), so nothing was ever sent to Helcim.
 */
export type InvoiceChargeAttemptOutcome = 'APPROVED' | 'DECLINED' | 'ERROR' | 'REJECTED';

/**
 * One immutable audit row per charge attempt against an invoice, written on every
 * attempt that got past the invoice-load step — whether or not the charge actually
 * reached Helcim. The backend returns them oldest → newest, so the list reads as
 * the invoice's charge lifecycle in order.
 *
 * These rows carry masked card data and processor internals meant for operator
 * reconciliation; the query behind them is internal-only.
 */
export interface InvoiceChargeAttempt {
  id: string;
  outcome: InvoiceChargeAttemptOutcome;
  /**
   * Amount submitted to Helcim. Null on REJECTED rows where the guard fired before
   * the total was computed.
   */
  chargedAmount: number | null;
  /** ISO-8601 timestamp of the attempt. */
  createdAt: string;
  helcimTransactionId: string | null;
  helcimInvoiceNumber: string | null;
  /** Helcim's own raw status string for the transaction, e.g. "APPROVED". */
  helcimStatus: string | null;
  approvalCode: string | null;
  // The backend also exposes avsResponse / cvvResponse. They're deliberately not
  // selected or typed here: the table doesn't show them, so there's no reason to
  // pull that card-verification detail over the wire.
  cardType: string | null;
  /** Last four digits of the card charged — the only card digits we ever hold. */
  cardLast4: string | null;
  /** HTTP status of the failed Helcim call. Only set on ERROR rows. */
  errorHttpStatus: number | null;
  /** Triage summary. Populated only on ERROR and REJECTED; always null otherwise. */
  errorMessage: string | null;
}

/**
 * A single line on an invoice. Captures the product and the discount that was
 * applied at issue time, snapshotted from the contract so the invoice stays
 * immutable even if the contract changes.
 *
 * For manual invoices, lines can be ad-hoc: `displayName` and `price` are
 * supplied directly instead of being looked up from a catalog product.
 */
export interface InvoiceLineItem {
  id: string;
  /** Parent invoice id — denormalized onto the line for direct lookup. */
  invoiceId: string;
  startDate: string;
  endDate: string;
  /** Number of SKU units on this line, snapshotted from ContractContent.quantity at planning time. Fractional, 2 decimal places. */
  quantity: number;
  discountType: DiscountType | null;
  discountAmount: number;
  /** Display name. Comes from the linked product for catalog lines, or from the manual input for ad-hoc lines. */
  displayName: string;
  /** Per-unit price in USD. Comes from the linked product for catalog lines, or from the manual input for ad-hoc lines. */
  price: number;
}

/**
 * One line on a manual invoice. `displayName` and `price` are always required:
 * for catalog lines the caller copies them from the product (and also passes
 * `productSku` to link the line to the catalog entry); for ad-hoc lines the
 * caller supplies them directly and leaves `productSku` unset. Discount is
 * optional in either case.
 */
export interface ManualInvoiceLineItemInput {
  /** Set for catalog lines to link to the product; omit for ad-hoc lines. */
  productSku?: string;
  displayName: string;
  price: number;
  /** Required per manual line item; defaults to 1 in the UI unless the operator overrides. Fractional, 2 decimal places. */
  quantity: number;
  startDate: string;
  endDate: string;
  discountType?: DiscountType | null;
  discountAmount?: number;
}

/**
 * Input shape for the createManualInvoice mutation. Even though the invoice is
 * raised by hand, every invoice — including MANUAL ones — must be attached to
 * a contract, so `contractId` is required.
 */
export interface CreateManualInvoiceInput {
  clientId: string;
  contractId: string;
  /** YYYY-MM-DD; when the invoice should be charged. */
  expectedChargeDate: string;
  lineItems: ManualInvoiceLineItemInput[];
}

export interface VoidInvoiceInput {
  invoiceId: string;
}

export interface ProcessHelcimInvoicePaymentInput {
  invoiceId: string;
}

export interface RefundInvoiceInput {
  invoiceId: string;
}

/**
 * Result of a Helcim charge attempt. `status` is the processor's own transaction
 * outcome (e.g. APPROVED, DECLINED) — separate from the invoice's lifecycle status,
 * which the backend updates as a side effect of the charge.
 */
export interface HelcimPaymentResult {
  transactionId: string;
  status: string;
  amount: number;
  currency: string;
}

/**
 * Manual reset for an invoice stuck in PROCESSING — the operator must supply a
 * free-text `note` explaining what they verified with the payment processor
 * before unsticking (kept as an audit trail on the backend).
 */
export interface ResetStuckInvoiceToPendingInput {
  invoiceId: string;
  note: string;
}

/**
 * Manual mark-as-paid for an invoice stuck in PROCESSING when the operator has
 * confirmed a real charge went through at the processor. The operator supplies
 * the transaction id and actual charge date they observed, plus an audit note.
 */
export interface MarkStuckInvoicePaidInput {
  invoiceId: string;
  paymentTransactionId: string;
  /** YYYY-MM-DD; when the processor actually charged the card. */
  actualChargeDate: string;
  note: string;
}

/**
 * Manual mark-as-denied for an invoice stuck in PROCESSING when the operator
 * has confirmed at the processor that the charge was declined. `chargeStatus`
 * is the processor's raw status string (e.g. "DECLINED"), not the app's own
 * ChargeStatus enum — it's stored verbatim for the audit trail.
 */
export interface MarkStuckInvoicePaymentDeniedInput {
  invoiceId: string;
  paymentTransactionId: string;
  chargeStatus: string;
  note: string;
}

/**
 * One expected charge against a contract.
 *
 * When a contract is finalized, the backend generates the full series of
 * invoices for the contract term. Each invoice has an expected charge date;
 * billing automation processes them on schedule.
 */
export interface Invoice {
  id: string;
  origin: InvoiceOrigin;
  status: InvoiceStatus;
  /** YYYY-MM-DD; when this invoice is scheduled to be charged. */
  expectedChargeDate: string;
  /** YYYY-MM-DD; when the invoice was actually charged. Null until charged. */
  actualChargeDate: string | null;
  /** External payment processor transaction reference. Null until charged. */
  paymentTransactionId: string | null;
  /** Helcim's own invoice number for the charge attempt. Null until Helcim assigns one. */
  helcimInvoiceNumber: string | null;
  chargeStatus: ChargeStatus;
  /** Sum of the line items after discounts, before tax. */
  subtotal: number;
  /** Sales tax applied to the invoice. */
  taxAmount: number;
  /** Amount actually charged (subtotal + taxAmount). */
  total: number;
  lineItems: InvoiceLineItem[];
}
